import React from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { BudgetRequest } from '@models/budget.model';
import useFormattedCurrency from '@hooks/useFormattedCurrency';

interface BidActionItemProps {
  budgetRequest: BudgetRequest;
  onView: (budgetRequestId: string) => void;
}

/**
 * Sprint 16, story-076. One bid row in the "Needs your action" block, sourced from
 * `usePendingBudgetRequests` — the same hook that already produces the sidebar's Bids badge.
 * Link-only: no inline action here (a bid's own resolution happens on `/budgets`).
 */
export const BidActionItem: React.FC<BidActionItemProps> = ({ budgetRequest, onView }) => {
  const formattedMaxBudget = useFormattedCurrency(budgetRequest.maxBudgetAmount ?? null);
  const originatorName = budgetRequest.originatorOrganization?.name || 'Unknown organization';
  const taskName = budgetRequest.task?.name || 'Untitled task';

  const handleClick = () => onView(budgetRequest.budgetRequestId);
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  return (
    <div
      data-testid={`needs-your-action-bid-${budgetRequest.budgetRequestId}`}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className="flex items-center justify-between gap-2 p-3 bg-white border border-gray-200 rounded-md cursor-pointer hover:bg-gray-50">
      <div className="min-w-0">
        <div className="font-semibold text-gray-900 text-sm truncate">
          Bid request from {originatorName}
        </div>
        <div className="text-xs text-gray-600 truncate">
          {taskName}
          {budgetRequest.maxBudgetAmount != null && ` · Max bid ${formattedMaxBudget}`}
        </div>
      </div>
      <ChevronRightIcon fontSize="small" className="text-gray-400 flex-shrink-0" />
    </div>
  );
};

export default BidActionItem;
